import { baseApi } from "../../../../core/data/rtk/baseQuery";
import { ApiResponseType } from "../../../../core/data/rtk/apiResponseType";
import {
  AttendanceApiResponse,
  PaginatedAttendanceResponse,
  StudentAttendanceDetailsResponse,
} from "../types/attendanceTypes";

export const attendanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllAttendance: builder.query<
      ApiResponseType<PaginatedAttendanceResponse>,
      {
        date?: string;
        course?: string;
        batch?: string;
        page?: number;
        size?: number;
      }
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.date) queryParams.append("date", params.date);
        if (params?.course) queryParams.append("course", params.course);
        if (params?.batch) queryParams.append("batch", params.batch);
        if (params?.page) queryParams.append("page", params.page.toString());
        if (params?.size) queryParams.append("size", params.size.toString());

        return `student-attendance/get-all?${queryParams.toString()}`;
      },
      providesTags: ["Attendance"],
    }),
    getAttendanceById: builder.query<
      ApiResponseType<AttendanceApiResponse>,
      number
    >({
      query: (id) => `student-attendance/get/${id}`,
      providesTags: (result) => [{ type: "Attendance", id: result?.data.id }],
    }),
    getAttendanceByStudent: builder.query<
      ApiResponseType<StudentAttendanceDetailsResponse>,
      { id: number; date?: string; month?: string }
    >({
      query: ({ id, date, month }) => {
        const queryParams = new URLSearchParams();
        if (date) queryParams.append("date", date);
        if (month) queryParams.append("month", month);

        const queryString = queryParams.toString();
        return `student-attendance/get-by-student/${id}${
          queryString ? `?${queryString}` : ""
        }`;
      },
      providesTags: ["Attendance"],
    }),
    createAttendance: builder.mutation<
      ApiResponseType<AttendanceApiResponse>,
      { student_id: number; attendance_status: string; date: string }
    >({
      query: (payload) => ({
        url: "student-attendance/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Attendance"],
    }),
    createBulkAttendance: builder.mutation<
      ApiResponseType<AttendanceApiResponse[]>,
      {
        attendance: Array<{
          student_id: number;
          attendance_status: string;
          date: string;
        }>;
      }
    >({
      query: (payload) => ({
        url: "student-attendance/bulk/save",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Attendance"],
    }),
    updateAttendance: builder.mutation<
      ApiResponseType<AttendanceApiResponse>,
      {
        id: number;
        student_id: number;
        attendance_status: string;
        date: string;
      }
    >({
      query: ({ id, ...payload }) => ({
        url: `student-attendance/update/${id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (result) => [
        { type: "Attendance", id: result?.data.id },
      ],
    }),
    updateBulkAttendance: builder.mutation<
      ApiResponseType<AttendanceApiResponse[]>,
      {
        attendance: Array<{
          id: number;
          student_id: number;
          attendance_status: string;
          date: string;
        }>;
      }
    >({
      query: (payload) => ({
        url: "student-attendance/bulk/update",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["Attendance"],
    }),
    deleteAttendance: builder.mutation<ApiResponseType<void>, number>({
      query: (id) => ({
        url: `student-attendance/delete/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Attendance"],
    }),
  }),
});

export const {
  useGetAllAttendanceQuery,
  useGetAttendanceByIdQuery,
  useGetAttendanceByStudentQuery,
  useCreateAttendanceMutation,
  useCreateBulkAttendanceMutation,
  useUpdateAttendanceMutation,
  useUpdateBulkAttendanceMutation,
  useDeleteAttendanceMutation,
} = attendanceApi;
